<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-user-tie"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">{{ isEdit ? 'Edit Attorney' : 'Add Attorney' }}</div>
          <div class="panel-header__sub">{{ isEdit ? 'Update attorney and contact details' : 'Register a new attorney' }}</div>
        </div>
      </div>

      <div class="form-body">

        <!-- Section: Attorney Details -->
        <div class="form-section">
          <div class="section-header">
            <i class="fa fa-id-card section-header__icon"></i>
            <span>Attorney Details</span>
          </div>
          <div class="field-grid">
            <div class="field-group" :class="{ 'field-group--error': errors['name'] }">
              <label class="field-label">Attorney Name <span class="req">*</span></label>
              <input v-model="name" type="text" class="field-input" placeholder="Enter attorney name" />
              <span class="field-error" v-if="errors['name']">{{ errors['name'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['contact_person_name'] }">
              <label class="field-label">Contact Person</label>
              <input v-model="contact_person_name" type="text" class="field-input" placeholder="Enter contact person name" />
              <span class="field-error" v-if="errors['contact_person_name']">{{ errors['contact_person_name'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['email'] }">
              <label class="field-label">Email <span class="req">*</span></label>
              <input v-model="email" type="email" class="field-input" placeholder="Enter email address" />
              <span class="field-error" v-if="errors['email']">{{ errors['email'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['contact_no'] }">
              <label class="field-label">Contact No.</label>
              <input v-model="contact_no" type="text" v-mask="'(###)-###-####'" class="field-input" placeholder="(###)-###-####" />
              <span class="field-error" v-if="errors['contact_no']">{{ errors['contact_no'][0] }}</span>
            </div>
          </div>

          <div class="toggle-row">
            <label class="switch-wrap">
              <input type="checkbox" v-model="is_active" />
              <span class="slider"></span>
            </label>
            <span class="toggle-label">Active</span>
          </div>

          <div class="approval-row" v-if="isEdit">
            <template v-if="is_approved">
              <span class="badge-pill approved">
                <i class="fa fa-check-circle"></i> Approved to use service
              </span>
              <button class="approve-btn approve-btn--revoke" @click="revokeApproval">
                <i class="fa fa-ban"></i> Revoke
              </button>
            </template>
            <template v-else>
              <span class="badge-pill pending">
                <i class="fa fa-clock-o"></i> Pending approval
              </span>
              <button class="approve-btn" @click="approveAttorney">
                <i class="fa fa-check"></i> Approve
              </button>
            </template>
          </div>
        </div>

        <!-- Section: Address -->
        <div class="form-section">
          <div class="section-header">
            <i class="fa fa-map-marker section-header__icon"></i>
            <span>Address</span>
          </div>
          <div class="field-grid">
            <div class="field-group" :class="{ 'field-group--error': errors['address1'] }">
              <label class="field-label">Address Line 1</label>
              <input v-model="address1" type="text" class="field-input" placeholder="Street address" />
              <span class="field-error" v-if="errors['address1']">{{ errors['address1'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['address2'] }">
              <label class="field-label">Address Line 2</label>
              <input v-model="address2" type="text" class="field-input" placeholder="Apt, suite, etc." />
              <span class="field-error" v-if="errors['address2']">{{ errors['address2'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['city'] }">
              <label class="field-label">City</label>
              <input v-model="city" type="text" class="field-input" placeholder="City" />
              <span class="field-error" v-if="errors['city']">{{ errors['city'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['state'] }">
              <label class="field-label">State</label>
              <input v-model="state" type="text" class="field-input" placeholder="State" />
              <span class="field-error" v-if="errors['state']">{{ errors['state'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['zipcode'] }">
              <label class="field-label">Zipcode</label>
              <input v-model="zipcode" type="text" class="field-input" placeholder="Zipcode" />
              <span class="field-error" v-if="errors['zipcode']">{{ errors['zipcode'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['country'] }">
              <label class="field-label">Country</label>
              <input v-model="country" type="text" class="field-input" placeholder="Country" />
              <span class="field-error" v-if="errors['country']">{{ errors['country'][0] }}</span>
            </div>
          </div>

          <!-- Google Map -->
          <div class="map-wrap">
            <label class="field-label">Select Address on Map</label>
            <div class="map-box">
              <keep-alive>
                <GoogleMap
                  ref="clientmap"
                  @getFullAddress="setFullAddress"
                  @dragPointer="setLocation"
                  @onSearchClick="setLocation"
                />
              </keep-alive>
            </div>
          </div>
        </div>

        <!-- Section: Documents -->
        <div class="form-section" v-if="isEdit">
          <div class="section-header">
            <i class="fa fa-file-text section-header__icon"></i>
            <span>Documents</span>
          </div>

          <div class="document-row">
            <div class="document-actions">
              <label class="qr-btn upload-btn-input">
                <input type="file" accept="image/*,application/pdf" multiple @change="onComputerFileChange" />
                <span v-if="uploadingDirect"><i class="fa fa-spinner fa-spin"></i> Uploading…</span>
                <span v-else><i class="fa fa-upload"></i> Upload from computer</span>
              </label>

              <button class="qr-btn" @click="openQrModal">
                <i class="fa fa-qrcode"></i> Upload from phone
              </button>
            </div>
          </div>

          <div class="doc-list">
            <div class="doc-empty" v-if="documents.length === 0">No documents uploaded yet</div>
            <div class="doc-item" v-for="doc in documents" :key="doc.id">
              <i class="fa fa-file-o doc-item__icon"></i>
              <div class="doc-item__info">
                <div class="doc-item__name">{{ doc.original_filename || 'Document' }}</div>
                <div class="doc-item__meta">{{ doc.uploaded_by_name ? 'Uploaded by ' + doc.uploaded_by_name + ' · ' : '' }}{{ formatDate(doc.uploaded_at) }}</div>
              </div>
              <div class="doc-item__actions">
                <a :href="doc.file" target="_blank" class="doc-action-btn" title="View">
                  <i class="fa fa-eye"></i>
                </a>
                <button class="doc-action-btn" title="Download" @click="downloadDocument(doc)">
                  <i class="fa fa-download"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- QR upload modal -->
    <transition name="modal-fade">
      <div class="del-overlay" v-if="qrModal.show" @click.self="closeQrModal">
        <div class="qr-box">
          <div class="qr-title">Scan to upload document</div>
          <div class="qr-sub">Scan this code with your phone's camera to open the upload page and attach a document to this attorney.</div>

          <div class="qr-code-wrap" v-if="qrModal.uploadUrl">
            <qr-code :text="qrModal.uploadUrl" :size="180" bg-color="#ffffff" error-level="M"></qr-code>
          </div>
          <div class="qr-loading" v-else>
            <div class="state-spinner"></div>
          </div>

          <div class="qr-status">
            <template v-if="qrModal.uploaded">
              <i class="fa fa-check-circle qr-status__ok"></i> Document uploaded successfully
            </template>
            <template v-else-if="qrModal.uploadUrl">
              <i class="fa fa-clock-o"></i> Waiting for upload…
            </template>
          </div>

          <button class="del-cancel" @click="closeQrModal">Close</button>
        </div>
      </div>
    </transition>

    <!-- Floating save bar -->
    <div class="save-bar">
      <button class="save-bar__back" @click="goBack()">
        <i class="fa fa-arrow-left"></i> Back
      </button>
      <div class="save-bar__right">
        <template v-if="!isEdit">
          <button class="save-bar__secondary" ref="kt_save_and_add_service"
            @click="store('kt_save_and_add_service', 'SAA')">
            <i class="fa fa-plus"></i> Save &amp; Add Another
          </button>
          <button class="save-bar__primary" ref="kt_save_service"
            @click="store('kt_save_service', 'S')">
            <i class="fa fa-check"></i> Save
          </button>
        </template>
        <button v-else class="save-bar__primary" ref="kt_update_service"
          @click="update('kt_update_service')">
          <i class="fa fa-check"></i> Update
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import moment from "moment";
import {
  CREATE_ATTORNEY,
  FETCH_ATTORNEY_DETAILS,
  APPROVE_ATTORNEY,
  REVOKE_ATTORNEY_APPROVAL,
  GENERATE_ATTORNEY_UPLOAD_TOKEN,
  GET_ATTORNEY_UPLOAD_STATUS,
  UPLOAD_ATTORNEY_DOCUMENT_DIRECT
} from "@/core/services/store/actions.type";
import GoogleMap from "@/components/GoogleMap.vue";
import { resizeImageFile } from "@/core/services/imageresize";
import VueQRCodeComponent from "vue-qrcode-component";

export default {
  components: { GoogleMap, "qr-code": VueQRCodeComponent },
  data() {
    return {
      errors: [],
      isEdit: false,
      id: 0,
      name: "",
      email: "",
      contact_person_name: "",
      contact_no: "",
      address1: "",
      address2: "",
      city: "",
      zipcode: "",
      country: "",
      state: "",
      lat: "",
      lng: "",
      is_active: true,
      is_approved: false,
      documents: [],
      uploadingDirect: false,
      qrModal: {
        show: false,
        token: "",
        uploadUrl: "",
        uploaded: false
      },
      qrPollTimer: null,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "admin.dashboard" },
        { id: 2, title: "Attorney", route: "admin.attorney.index" },
        { id: 3, title: "Create New", route: "" }
      ]
    };
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.isEdit = this.$route.name === "admin.attorney.edit";
    if (this.isEdit) this.fetchAttorneyDetails();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    clearForm() {
      this.id = 0; this.name = ""; this.email = ""; this.contact_person_name = "";
      this.contact_no = ""; this.address1 = ""; this.address2 = "";
      this.zipcode = ""; this.country = ""; this.state = ""; this.city = "";
      this.lat = ""; this.lng = ""; this.is_active = true; this.is_approved = false;
    },
    loadingButton(ref) {
      const btn = this.$refs[ref];
      btn.disabled = true;
      btn.classList.add("spinner", "spinner-light", "spinner-right");
    },
    closeBtnLoad(ref) {
      const btn = this.$refs[ref];
      btn.classList.remove("spinner", "spinner-light", "spinner-right");
      btn.disabled = false;
    },
    payload() {
      return {
        id: this.id, name: this.name, email: this.email,
        contact_person_name: this.contact_person_name, contact_no: this.contact_no,
        address1: this.address1, address2: this.address2, zipcode: this.zipcode,
        country: this.country, state: this.state, city: this.city,
        lat: this.lat, lng: this.lng, is_active: this.is_active
      };
    },
    setLocation(location) {
      this.lng = location.lng;
      this.lat = location.lat;
      this.city = location.city.long_name;
      this.address1 = location.formatted_address;
      this.zipcode = location.postal_code.long_name;
      this.country = location.country.long_name;
      this.state = location.state.long_name;
    },
    setFullAddress(data) { this.address1 = data; },
    update(ref) {
      this.loadingButton(ref);
      this.$store.dispatch(CREATE_ATTORNEY, this.payload())
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          this.$router.push({ name: "admin.attorney.index" });
        })
        .catch(err => { this.errors = err; this.closeBtnLoad(ref); });
    },
    store(ref, saveType) {
      this.loadingButton(ref);
      this.$store.dispatch(CREATE_ATTORNEY, this.payload())
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          if (saveType === "S") {
            this.$router.push({ name: "admin.attorney.index" });
          } else {
            this.clearForm();
          }
        })
        .catch(err => { this.errors = err; this.closeBtnLoad(ref); });
    },
    goBack() {
      this.$router.push({ name: "admin.attorney.index" });
    },
    fetchAttorneyDetails() {
      this.breadCrumbs[2].title = "Edit";
      this.$store.dispatch(FETCH_ATTORNEY_DETAILS, this.$route.params.id)
        .then(data => {
          const d = data.data;
          this.id = d.id; this.name = d.name; this.email = d.email;
          this.contact_person_name = d.contact_person_name;
          this.contact_no = d.contact_no;
          this.address1 = d.address1; this.address2 = d.address2;
          this.zipcode = d.zipcode; this.country = d.country;
          this.state = d.state; this.city = d.city;
          this.lat = d.lat; this.lng = d.lng;
          this.is_active = d.is_active;
          this.is_approved = d.is_approved;
          this.documents = d.documents || [];
          if (d.lat && d.lng) {
            this.$refs.clientmap.defaultMarkerPosition(Number(d.lat), Number(d.lng));
          }
        });
    },
    formatDate(date) {
      return date ? moment(date).format("MM/DD/YYYY hh:mm A") : "";
    },
    downloadDocument(doc) {
      fetch(doc.file)
        .then(res => res.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", doc.original_filename || "document");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.$toastr.e("Unable to download document");
        });
    },
    onComputerFileChange(e) {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      this.uploadingDirect = true;
      const uploadOne = file =>
        resizeImageFile(file).then(resized =>
          this.$store.dispatch(UPLOAD_ATTORNEY_DOCUMENT_DIRECT, { id: this.id, file: resized })
        );

      Promise.all(files.map(uploadOne))
        .then(results => {
          results.forEach(res => this.documents.unshift(res.data));
          this.uploadingDirect = false;
          this.$toastr.s(files.length > 1 ? "Documents uploaded successfully" : "Document uploaded successfully");
        })
        .catch(err => {
          this.uploadingDirect = false;
          this.$toastr.e((err && err.msg) || "Upload failed");
        });
      e.target.value = "";
    },
    approveAttorney() {
      this.$store.dispatch(APPROVE_ATTORNEY, this.id)
        .then(res => {
          this.is_approved = true;
          this.$toastr.s(res.msg);
        })
        .catch(err => {
          this.$toastr.e((err && err.msg) || "Unable to approve attorney");
        });
    },
    revokeApproval() {
      this.$store.dispatch(REVOKE_ATTORNEY_APPROVAL, this.id)
        .then(res => {
          this.is_approved = false;
          this.$toastr.s(res.msg);
        })
        .catch(err => {
          this.$toastr.e((err && err.msg) || "Unable to revoke approval");
        });
    },
    openQrModal() {
      this.qrModal = { show: true, token: "", uploadUrl: "", uploaded: false };
      this.$store.dispatch(GENERATE_ATTORNEY_UPLOAD_TOKEN, this.id).then(res => {
        this.qrModal.token = res.data.token;
        this.qrModal.uploadUrl = res.data.upload_url;
        this.startPolling();
      });
    },
    closeQrModal() {
      this.qrModal.show = false;
      this.stopPolling();
    },
    startPolling() {
      this.stopPolling();
      this.qrPollTimer = setInterval(() => {
        this.$store.dispatch(GET_ATTORNEY_UPLOAD_STATUS, this.qrModal.token).then(res => {
          if (res.data.is_used) {
            this.qrModal.uploaded = true;
            if (res.data.document) this.documents.unshift(res.data.document);
            this.stopPolling();
          }
        });
      }, 3000);
    },
    stopPolling() {
      if (this.qrPollTimer) {
        clearInterval(this.qrPollTimer);
        this.qrPollTimer = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 12px 12px 96px;
  font-family: $font;
}

.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px; height: 48px; border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 18px; flex-shrink: 0;
  }
  &__text { flex: 1; }
  &__title { font-size: 16px; font-weight: 700; color: $dark; line-height: 1.2; }
  &__sub   { font-size: 12px; color: #7a8499; margin-top: 3px; }
}

.form-body { padding: 8px 0; }

.form-section {
  padding: 20px 28px 24px;
  & + & { border-top: 1px solid $border; }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: $dark;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;

  &__icon { font-size: 13px; color: $blue; }
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
}

.field-group {
  display: flex; flex-direction: column; gap: 5px;
  &--error .field-input { border-color: #b42318; }
}

.field-label {
  font-size: 12px; font-weight: 600;
  color: #5a6278; font-family: $font;
  .req { color: #b42318; }
}

.field-input {
  height: 38px;
  border: 1.5px solid $border; border-radius: 8px;
  padding: 0 12px; font-size: 13px;
  color: $dark; font-family: $font;
  outline: none; background: #fff; width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus { border-color: $blue; box-shadow: 0 0 0 3px rgba(56,154,212,0.1); }
  &::placeholder { color: #b0bac9; }
}

.field-error { font-size: 11px; color: #b42318; }

.map-wrap {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.map-box {
  border: 1.5px solid $border;
  border-radius: 12px;
  overflow: hidden;
  min-height: 320px;
}

/* ── toggle switch ── */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 18px;

  .toggle-label { font-size: 13px; color: #4a5568; font-weight: 500; }
}

.approval-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;

  &.approved { background: #c6f6d5; color: #276749; }
  &.pending { background: #fefcbf; color: #7b6e1a; }
}

.approve-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 14px;
  background: #389ad4;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #2d85bb; }

  &--revoke {
    background: #fff;
    color: #b42318;
    border: 1.5px solid #b42318;

    &:hover { background: rgba(180, 35, 24, 0.08); }
  }
}

.switch-wrap {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;

  input { opacity: 0; width: 0; height: 0; }

  .slider {
    position: absolute;
    inset: 0;
    background: #cbd5e0;
    border-radius: 22px;
    cursor: pointer;
    transition: background 0.2s;

    &::before {
      content: '';
      position: absolute;
      width: 16px; height: 16px;
      left: 3px; top: 3px;
      background: #fff;
      border-radius: 50%;
      transition: transform 0.2s;
    }
  }

  input:checked + .slider { background: $blue; }
  input:checked + .slider::before { transform: translateX(16px); }
}

/* ── document section ── */
.document-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.doc-empty {
  font-size: 13px;
  color: #a0aab8;
  padding: 12px 0;
}

.document-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.doc-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.doc-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1.5px solid $border;
  border-radius: 10px;
  background: #fafbfd;

  &__icon {
    font-size: 18px;
    color: $blue;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: $dark;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    font-size: 11.5px;
    color: #7a8499;
    margin-top: 2px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }
}

.doc-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 7px;
  border: 1.5px solid $border;
  background: #fff;
  color: $blue;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.15s, border-color 0.15s;

  &:hover { background: rgba(56,154,212,0.08); border-color: $blue; }
}

.qr-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 36px;
  padding: 0 16px;
  margin: 0;
  background: #fff;
  color: $blue;
  border: 1.5px solid $blue;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  line-height: 1;
  vertical-align: middle;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: rgba(56,154,212,0.08); }
}

.upload-btn-input input {
  display: none;
}

.upload-btn-input span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

/* ── QR modal ── */
.del-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(13, 27, 62, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-box {
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  width: 340px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(13, 27, 62, 0.2);
}

.qr-title { font-size: 16px; font-weight: 700; color: $dark; margin-bottom: 8px; }
.qr-sub { font-size: 12.5px; color: #7a8499; line-height: 1.5; margin-bottom: 20px; }

.qr-code-wrap {
  display: flex;
  justify-content: center;
  padding: 12px;
  margin-bottom: 16px;
}

.qr-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px;
  margin-bottom: 16px;
}

.state-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #c5d8f5;
  border-top-color: $blue;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.qr-status {
  font-size: 13px;
  color: #7a8499;
  margin-bottom: 20px;

  &__ok { color: #276749; }
}

.del-cancel {
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1.5px solid $border;
  background: #fff;
  color: $dark;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { background: #f4f6fb; }
}

.modal-fade-enter-active,
.modal-fade-leave-active { transition: opacity 0.2s; }

.modal-fade-enter,
.modal-fade-leave-to { opacity: 0; }

/* ── floating save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  left: 50%;
  transform: translateX(-40%);
  background: #1a2340;
  border-radius: 14px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(13,27,62,0.3);
  z-index: 100;

  &__right { display: flex; align-items: center; gap: 10px; }

  &__back {
    display: inline-flex; align-items: center; gap: 7px;
    background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.75);
    border: 1px solid rgba(255,255,255,0.15); border-radius: 9px;
    padding: 8px 16px; font-size: 13px; font-weight: 600;
    font-family: $font; cursor: pointer; transition: background 0.15s;
    &:hover { background: rgba(255,255,255,0.14); }
  }

  &__secondary {
    display: inline-flex; align-items: center; gap: 7px;
    background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.8);
    border: 1px solid rgba(255,255,255,0.2); border-radius: 9px;
    padding: 8px 18px; font-size: 13px; font-weight: 600;
    font-family: $font; cursor: pointer; transition: background 0.15s;
    &:hover { background: rgba(255,255,255,0.14); }
    &:disabled { opacity: 0.5; cursor: default; }
  }

  &__primary {
    display: inline-flex; align-items: center; gap: 7px;
    background: $blue; color: #fff; border: none; border-radius: 9px;
    padding: 8px 22px; font-size: 13px; font-weight: 600;
    font-family: $font; cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;
    &:hover { background: #2d85bb; box-shadow: 0 4px 14px rgba(56,154,212,0.4); }
    &:disabled { opacity: 0.6; cursor: default; box-shadow: none; }
  }
}
</style>
